// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore Turf 6.5's package exports do not expose its bundled declarations to TypeScript's bundler resolution.
import { convertLength } from "@turf/helpers";
import { UnitOfMeasurement } from "@iventis/domain-model/model/unitOfMeasurement";
import { metersInKilometer, feetInMile } from "./constants";

/**
 * Converts length value to Metric or imperial and then to an easily readable value. eg) "10,000 m" changes to "10 km"
 * @param length - Measurement value in meters
 * @param unitOfMeasurement - Metric or imperial system
 * @returns converted length with unit appended. eg) "10 m" or "7.2 mi"
 */
export function convertLengthMeasurement(length: number, unitOfMeasurement: UnitOfMeasurement) {
    switch (unitOfMeasurement) {
        case UnitOfMeasurement.Metric:
            return convertMetric(length);
        case UnitOfMeasurement.Imperial:
            return convertImperial(length);
        default:
            throw new Error(`Unit of measurement ${unitOfMeasurement} is not supported`);
    }
}

// eslint-disable-next-line prettier/prettier
function convertMetric(length: number): `${number} m` | `${number} km` {
    if (length >= metersInKilometer) {
        return `${(length / metersInKilometer).toFixed(2) as `${number}`} km`;
    }
    return `${length.toFixed(2) as `${number}`} m`;
}

// eslint-disable-next-line prettier/prettier
function convertImperial(length: number): `${number} mi` | `${number} ft` {
    const lengthFeet = convertLength(length, "meters", "feet");
    if (lengthFeet >= feetInMile) {
        return `${(lengthFeet / feetInMile).toFixed(2) as `${number}`} mi`;
    }
    return `${lengthFeet.toFixed(2) as `${number}`} ft`;
}

// Turf rejects negative lengths, so convert the absolute value and restore the sign to support values such as negative offsets
export function convertLengthMetersToFeet(metersValue: number) {
    return Math.sign(metersValue) * convertLength(Math.abs(metersValue), "meters", "feet");
}

export function convertLengthFeetToMeters(feetValue: number) {
    const value = feetValue ?? 0;
    return Math.sign(value) * convertLength(Math.abs(value), "feet", "meters");
}

/**
 * Converts a length in metres to the unit an input shows. Inputs always use feet or metres, never miles or kilometres
 * @param metres - Length in metres
 * @param unitOfMeasurement - Metric or imperial system
 * @returns the length in feet for imperial, otherwise metres
 */
export function toDisplayLength(metres: number, unitOfMeasurement: UnitOfMeasurement) {
    if (metres == null) {
        return metres;
    }
    return unitOfMeasurement === UnitOfMeasurement.Imperial ? convertLengthMetersToFeet(metres) : metres;
}

/**
 * Converts a length entered in an input back to metres, which is how every length is stored
 * @param value - Length in feet for imperial, otherwise metres
 * @param unitOfMeasurement - Metric or imperial system
 * @returns the length in metres
 */
export function fromDisplayLength(value: number, unitOfMeasurement: UnitOfMeasurement) {
    if (value == null) {
        return value;
    }
    return unitOfMeasurement === UnitOfMeasurement.Imperial ? convertLengthFeetToMeters(value) : value;
}

/**
 * Converts min and max limits in metres to the unit an input shows. Imperial limits are rounded inwards to 2 decimal places,
 * so they read cleanly in error messages and any value inside them is still inside the metre limits once converted back
 * @param minMetres - Minimum length in metres
 * @param maxMetres - Maximum length in metres
 * @param unitOfMeasurement - Metric or imperial system
 * @returns the min and max limits in feet for imperial, otherwise metres
 */
export function toDisplayLengthLimits(minMetres: number, maxMetres: number, unitOfMeasurement: UnitOfMeasurement) {
    if (unitOfMeasurement !== UnitOfMeasurement.Imperial) {
        return { minValue: minMetres, maxValue: maxMetres };
    }
    return {
        minValue: minMetres == null ? minMetres : Math.ceil(toDisplayLength(minMetres, unitOfMeasurement) * 100) / 100,
        maxValue: maxMetres == null ? maxMetres : Math.floor(toDisplayLength(maxMetres, unitOfMeasurement) * 100) / 100,
    };
}
